// Tracking utility for Google Analytics, GTM, and Meta Pixel conversion events

const GA_MEASUREMENT_ID = 'G-W4EN6L59SQ';

// ──────────────────────────────────────────────
// LGPD — Google Consent Mode v2
// ──────────────────────────────────────────────

// Roda síncrono no boot (main.tsx) ANTES de qualquer gtag.js carregar.
// Garante que GA4/Ads recebam analytics_storage='denied' como default — a injeção
// do gtag via useScriptInjection só dispara depois.
export const bootstrapConsentMode = () => {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }

  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  });
};

// Chamado pelo ConsentProvider quando o usuário aceita/recusa.
export const updateGA4Consent = (granted: boolean) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const value = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
};

// Google Analytics event tracking
export const trackGA4Event = (eventName: string, params?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
};

// Google Tag Manager dataLayer push
export const trackGTMEvent = (event: string, data?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event,
      ...data,
    });
  }
};

// Meta Pixel event tracking
export const trackMetaEvent = (eventName: string, params?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, params);
  }
};

// Track custom Meta event
export const trackMetaCustomEvent = (eventName: string, params?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('trackCustom', eventName, params);
  }
};

// ──────────────────────────────────────────────
// GA4 Auth Bridge — vincula user_id à jornada GA4
// ──────────────────────────────────────────────

export const setGA4User = (userId: string, properties?: {
  tenant_id?: string;
  tenant_type?: string;
  role?: string;
  acquisition_source?: string;
  acquisition_medium?: string;
  acquisition_campaign?: string;
}) => {
  if (typeof window === 'undefined' || !window.gtag) return;

  window.gtag('config', GA_MEASUREMENT_ID, { user_id: userId });

  if (properties) {
    window.gtag('set', 'user_properties', properties);
  }
};

export const clearGA4User = () => {
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('config', GA_MEASUREMENT_ID, { user_id: undefined });
  window.gtag('set', 'user_properties', {});
};

// ──────────────────────────────────────────────
// GA4 E-commerce — funil de monetização
// ──────────────────────────────────────────────

export interface GA4Item {
  item_id: string;
  item_name: string;
  item_category?: string;
  price?: number;
  quantity?: number;
}

export const trackGA4ViewItem = (item: GA4Item, currency = 'USD') => {
  trackGA4Event('view_item', {
    currency,
    value: item.price || 0,
    items: [{ ...item, quantity: item.quantity || 1 }],
  });
};

export const trackGA4AddToCart = (item: GA4Item, currency = 'USD') => {
  trackGA4Event('add_to_cart', {
    currency,
    value: item.price || 0,
    items: [{ ...item, quantity: item.quantity || 1 }],
  });
};

export const trackGA4Purchase = (transactionId: string, item: GA4Item, currency = 'USD') => {
  trackGA4Event('purchase', {
    transaction_id: transactionId,
    currency,
    value: item.price || 0,
    items: [{ ...item, quantity: item.quantity || 1 }],
  });
};

// ──────────────────────────────────────────────
// GA4 Ativação — funil de primeiro valor
// ──────────────────────────────────────────────

export const trackGA4Activation = (
  eventName: 'first_company_added' | 'first_report_viewed' | 'tutorial_complete' | 'pdf_downloaded',
  params?: Record<string, any>
) => {
  trackGA4Event(eventName, params);
};

// ──────────────────────────────────────────────
// Combined tracking — landing page (GA4 + GTM + Meta)
// ──────────────────────────────────────────────

export const trackConversion = (eventType: ConversionType, data?: ConversionData) => {
  const eventData = {
    ...data,
    timestamp: new Date().toISOString(),
  };

  switch (eventType) {
    case 'lead':
      trackGA4Event('generate_lead', eventData);
      trackGTMEvent('generate_lead', eventData);
      trackMetaEvent('Lead', eventData);
      break;

    case 'demo_request':
      trackGA4Event('schedule_demo', eventData);
      trackGTMEvent('schedule_demo', eventData);
      trackMetaEvent('Schedule', eventData);
      trackMetaCustomEvent('DemoRequest', eventData);
      break;

    case 'contact_form':
      trackGA4Event('contact_form_submit', eventData);
      trackGTMEvent('contact_form_submit', eventData);
      trackMetaEvent('Contact', eventData);
      break;

    case 'newsletter_signup':
      trackGA4Event('newsletter_signup', eventData);
      trackGTMEvent('newsletter_signup', eventData);
      trackMetaEvent('Subscribe', eventData);
      break;

    case 'page_view':
      trackGA4Event('page_view', eventData);
      trackMetaEvent('PageView', eventData);
      break;

    case 'button_click':
      trackGA4Event('button_click', eventData);
      trackGTMEvent('button_click', eventData);
      trackMetaCustomEvent('ButtonClick', eventData);
      break;

    case 'scroll_depth':
      trackGA4Event('scroll', eventData);
      trackGTMEvent('scroll_depth', eventData);
      break;

    case 'time_on_page':
      trackGA4Event('user_engagement', eventData);
      trackGTMEvent('time_on_page', eventData);
      break;

    default:
      trackGA4Event(eventType, eventData);
      trackGTMEvent(eventType, eventData);
      trackMetaCustomEvent(eventType, eventData);
  }
};

// Conversion types
export type ConversionType =
  | 'lead'
  | 'demo_request'
  | 'contact_form'
  | 'newsletter_signup'
  | 'page_view'
  | 'button_click'
  | 'scroll_depth'
  | 'time_on_page'
  | string;

// Conversion data interface
export interface ConversionData {
  email?: string;
  name?: string;
  company?: string;
  country?: string;
  sector?: string;
  value?: number;
  currency?: string;
  button_name?: string;
  page_path?: string;
  content_name?: string;
  content_category?: string;
  [key: string]: any;
}

// Track CTA clicks
export const trackCTAClick = (buttonName: string, location?: string) => {
  trackConversion('button_click', {
    button_name: buttonName,
    page_path: window.location.pathname,
    location,
  });
};

// ──────────────────────────────────────────────
// UTM Capture — persistência cross-session
// ──────────────────────────────────────────────

const UTM_STORAGE_KEY = 'tr360_utm';

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  captured_at?: string;
}

/** Captura UTMs da URL e persiste no localStorage (first-touch: não sobrescreve). */
export const captureUtmParams = (): UtmParams | null => {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const utm: UtmParams = {};
  let hasUtm = false;

  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const) {
    const val = params.get(key);
    if (val) {
      utm[key] = val;
      hasUtm = true;
    }
  }

  if (!hasUtm) return getStoredUtmParams();

  // First-touch: só grava se não existir
  const existing = localStorage.getItem(UTM_STORAGE_KEY);
  if (!existing) {
    utm.captured_at = new Date().toISOString();
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
  }

  return existing ? JSON.parse(existing) : utm;
};

/** Retorna UTMs armazenados (ou null). */
export const getStoredUtmParams = (): UtmParams | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(UTM_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/** Limpa UTMs após vinculação ao user_id (pós-primeiro-login). */
export const clearUtmParams = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(UTM_STORAGE_KEY);
};

/** Constrói URL com UTM params persistidos + query params da URL atual. */
export const buildUrlWithUtm = (basePath: string): string => {
  // Parse existing params from basePath
  const [path, existingQs] = basePath.split('?');
  const params = new URLSearchParams(existingQs || '');

  // Forward stored UTMs
  const utm = getStoredUtmParams();
  if (utm) {
    if (utm.utm_source) params.set('utm_source', utm.utm_source);
    if (utm.utm_medium) params.set('utm_medium', utm.utm_medium);
    if (utm.utm_campaign) params.set('utm_campaign', utm.utm_campaign);
    if (utm.utm_content) params.set('utm_content', utm.utm_content);
    if (utm.utm_term) params.set('utm_term', utm.utm_term);
  }

  // Forward any query params from the current URL (UTMs or custom)
  if (typeof window !== 'undefined') {
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.forEach((value, key) => {
      if (!params.has(key)) {
        params.set(key, value);
      }
    });
  }

  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};

// Initialize page view tracking for SPAs
export const trackPageView = (path?: string) => {
  const pagePath = path || window.location.pathname;

  trackGA4Event('page_view', {
    page_path: pagePath,
    page_title: document.title,
  });

  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
};
